
import { AccountController } from "@/app/ui/controllers/AccountController";
import { Vue } from "vue-class-component";
import generateLabel from "./label-pdf";
import generateLabelPlane from "./label-plane-pdf";
import generateLabelPlaneV2 from "./label-plane-v2-pdf";
import generateSPTJElektronikPDF from "./sptj-elektronik-pdf";
import { CargoData } from "@/domain/entities/Cargo";
import { CargoController } from "@/app/ui/controllers/CargoController";
import JsBarcode from "jsbarcode";
import { convertQRCode } from "@/app/ui/modules/receipt/module";
import { formatByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";

import {
  ColumnManifestData,
  HeaderManifestData,
  SectionManifestData
} from "@/domain/entities/MainApp";
import { generateManifestCargoV2 } from "./manifest-pdf-cargo-v2";
import capitalize from "lodash/capitalize";
import { FlagsUtilsCargo } from "@/feature-flags/flags-utils-cargo";
import { generatePti } from "./generate-pti";
import { generateSptjNonElectronic } from "./generate-sptj-non-electronic";
import { startCase } from "lodash";

export default class Print extends Vue {
  // get profile account
  get dataProfile() {
    return AccountController.accountData;
  }

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  async companyLogoPlaneLabel(prefix: any, short = false) {
    const logoLion = await import("@/app/ui/assets/images/logo-lion-air.png");
    const logoBatik = await import("@/app/ui/assets/images/logo-batik-air.png");
    const logoSuperjet = await import(
      "@/app/ui/assets/images/logo-super-jet-air.png"
    );
    const logoSuperAirJetShort = await import(
      "@/app/ui/assets/images/logo-super-air-jet-short.png"
    );
    const logoWings = await import("@/app/ui/assets/images/logo-wings-air.png");
    const logoLionParcel = await import(
      "@/app/ui/assets/images/logo-lion-parcel.png"
    );

    if (prefix.startsWith("JT")) {
      return logoLion.default;
    } else if (prefix.startsWith("ID")) {
      return logoBatik.default;
    } else if (prefix.startsWith("IU")) {
      return short ? logoSuperAirJetShort.default : logoSuperjet.default;
    } else if (prefix.startsWith("IW")) {
      return logoWings.default;
    } else if (prefix.startsWith("LP")) {
      return logoLionParcel.default;
    }
    return "";
  }

  async companyPlane(
    prefix: string,
    short = false
  ): Promise<{ logo: string; label: string }> {
    const logo = await this.companyLogoPlaneLabel(prefix, short);

    if (prefix.startsWith("JT")) {
      return {
        logo,
        label: "LION AIR"
      };
    } else if (prefix.startsWith("ID")) {
      return {
        logo,
        label: "BATIK AIR"
      };
    } else if (prefix.startsWith("IU")) {
      return {
        logo,
        label: "SUPER AIR JET"
      };
    } else if (prefix.startsWith("IW")) {
      return {
        logo,
        label: "WINGS AIR"
      };
    } else if (prefix.startsWith("LP")) {
      return {
        logo,
        label: "LION PARCEL"
      };
    }
    return {
      logo: "",
      label: ""
    };
  }

  getDetailCargo(id: string | number, isPrint: boolean, name: string) {
    if (name) {
      return CargoController.getDetailRetryCargo({
        cargoNo: String(id),
        isPrint: isPrint
      });
    }
    const isCargoNo =
      typeof id === "string" && /[A-Za-z-]/.test(id);
    if (isCargoNo) {
      return CargoController.getDetailCargoByCargoNo({
        cargoNo: String(id),
        isPrint: isPrint
      });
    }
    return CargoController.getDetailCargo({
      id,
      isPrint: isPrint
    });
  }

  private parseNumber(value: string) {
    if (typeof value === "number") return value;
    return value ? parseByCountry(value, { country: this.countryCode }) : 0;
  }

  async printData(cargoNumber: string | number, type: string, name = "") {
    const isPrint = true;
    const cargo: CargoData = await this.getDetailCargo(
      cargoNumber,
      isPrint,
      name
    );
    cargo.totalActualCargoGrossWeight = this.formatValueNumber(
      cargo.totalActualCargoGrossWeight
    );
    cargo.totalActualCargoVolumeWeight = this.formatValueNumber(
      cargo.totalActualCargoVolumeWeight
    );
    if (cargo.cargoNumber) {
      // barcode

      JsBarcode("#barcode", cargo.cargoNumber, {
        textAlign: "left",
        displayValue: false,
        fontSize: 16
      });

      // qr code
      const combineQRText = `${cargo.cargoNumber}#${
        cargo.vehiclePoliceNumber
      }#${cargo.originCityCode} - ${cargo.originCityName}#${
        cargo.destinationCityCode
      } - ${cargo.destinationCityName}#${cargo.airportDestinationCityCode} - ${
        cargo.airportDestinationCityName
      }#${cargo.cargoType}#${cargo.partnerName}#${cargo.cargoCommodity}#${
        cargo.cargoProduct
      }#${cargo.totalStt}#${cargo.cargoActualPiece}#${
        cargo.isUseNewCalculation
          ? cargo.totalActualCargoGrossWeight
          : cargo.totalGrossWeight
      }#${
        cargo.isUseNewCalculation
          ? cargo.totalActualCargoVolumeWeight
          : cargo.totalVolumeWeight
      }`;
      const QRCode = await convertQRCode(combineQRText);

      // logo
      const element: any = document;
      const companyLogo = element.getElementById("company-logo").src;
      const barcode = element.getElementById("barcode");
      const companyLogoPlaneLabel = await this.companyLogoPlaneLabel(
        cargo.vehiclePoliceNumber
      );

      // mapping data
      const data: any[] = [];

      cargo.detailStt.forEach((stt, index) =>
        data.push({
          no: index + 1,
          bagNo: stt.baggingVendorNumber || stt.baggingNumber || "-",
          sttNo: stt.sttNumber,
          sttElexysNo: stt.sttElexysNumber,
          pieces: stt.totalPieces,
          grossWeight: `${formatByCountry(stt.grossWeigth, { type: "weight", country: this.countryCode })} Kg`,
          volumeWeight: `${formatByCountry(stt.volumeWeight, { type: "weight", country: this.countryCode })} Kg`,
          productType: stt.productType,
          commodity: stt.commodity,
          origin: stt.origin,
          destination: stt.destination
        })
      );
      if (type === "label" && cargo.cargoType !== "plane")
        await generateLabel(cargo, companyLogo, barcode, QRCode);
      else this.labelPlane(cargo, companyLogoPlaneLabel, barcode);
    }
  }

  async labelPlane(
    cargo: CargoData,
    companyLogoPlaneLabel: string,
    barcode: any
  ) {
    if (FlagsUtilsCargo.new_label_v2.isEnabled()) {
      await generateLabelPlaneV2(cargo, companyLogoPlaneLabel);
    } else {
      await generateLabelPlane(cargo, companyLogoPlaneLabel, barcode);
    }
  }

  formatValueNumber(number: any) {
    const val = typeof number === "string"
      ? parseByCountry(number, { country: this.countryCode })
      : Number(number);
    return formatByCountry(val, { type: "weight", country: this.countryCode });
  }

  printManifestV2 = async (id: number) => {
    const dataManifest = await this.getDetailCargo(id, true, "");
    if (dataManifest) {
      dataManifest.totalActualCargoGrossWeight = this.formatValueNumber(
        dataManifest.totalActualCargoGrossWeight
      );
      dataManifest.totalActualCargoVolumeWeight = this.formatValueNumber(
        dataManifest.totalActualCargoVolumeWeight
      );
      JsBarcode("#barcode", dataManifest.cargoNumber, {
        textAlign: "left",
        displayValue: false,
        fontSize: 16
      });
      const element: any = document;
      const barcode = element.getElementById("barcode").src;
      // qr code
      const combineQRText = `${dataManifest.cargoNumber}#${
        dataManifest.vehiclePoliceNumber
      }#${dataManifest.originCityCode} - ${dataManifest.originCityName}#${
        dataManifest.destinationCityCode
      } - ${dataManifest.destinationCityName}#${
        dataManifest.airportDestinationCityCode
      } - ${dataManifest.airportDestinationCityName}#${
        dataManifest.cargoType
      }#${dataManifest.partnerName}#${dataManifest.cargoCommodity}#${
        dataManifest.cargoProduct
      }#${dataManifest.totalStt}#${dataManifest.cargoActualPiece}#${
        dataManifest.isUseNewCalculation
          ? dataManifest.totalActualCargoGrossWeight
          : dataManifest.totalGrossWeight
      }#${
        dataManifest.isUseNewCalculation
          ? dataManifest.totalActualCargoVolumeWeight
          : dataManifest.totalVolumeWeight
      }`;
      const QRCode = await convertQRCode(combineQRText);
      const data = new SectionManifestData({
        featureName: `Cargo Manifest [${dataManifest.cargoNumber}]`,
        headerDetailData: new HeaderManifestData({
          noBag: dataManifest.noBag,
          originCity: `${dataManifest.originCityCode} - ${dataManifest.originCityName}`,
          destinationCity: `${dataManifest.destinationCityCode} - ${dataManifest.destinationCityName}`,
          createdName: dataManifest.createdName,
          totalStt: dataManifest.totalStt,
          createdAtPdf: dataManifest.createdAt,
          updatedByName: dataManifest.updatedByName,
          updatedAtPdf: dataManifest.updatedAt,
          cargoCommodity: dataManifest.cargoCommodity,
          cargoProduct: dataManifest.cargoProduct,
          totalPiece: dataManifest.cargoActualPiece,
          totalActualCargoVolumeWeight:
            dataManifest.totalActualCargoVolumeWeight,
          totalActualCargoGrossWeight: dataManifest.totalActualCargoGrossWeight,
          totalActualCargoChargeableWeight: String(
            Math.max(
              this.parseNumber(dataManifest.totalActualCargoGrossWeight),
              this.parseNumber(dataManifest.totalActualCargoVolumeWeight)
            )
          ),
          totalGrossWeight: dataManifest.totalGrossWeight,
          totalVolumeWeight: dataManifest.totalVolumeWeight,
          sttTotalPiece: dataManifest.sttTotalPiece,
          updatedAt: dataManifest.updatedAt,
          updatedBy: dataManifest.updatedBy,
          cargoType: dataManifest.cargoType
        }),
        headerSection: [
          "noBag",
          "originCity",
          "destinationCity",
          "createdName",
          "createdAtPdf",
          "updatedByName",
          "updatedAtPdf",
          "partnerName",
          "departureDate",
          "arrivalDate",
          "cargoCommodity",
          "cargoProduct",
          "totalStt",
          "totalPiece",
          "totalGrossWeight",
          "totalVolumeWeight",
          "sttTotalPiece",
          "sttVolumeWeight",
          "sttGrossWeight",
          "totalActualCargoChargeableWeight"
        ],
        data: dataManifest.detailStt.map(
          (stt: any) =>
            new ColumnManifestData({
              bagNo: stt.baggingVendorNumber || stt.baggingNumber || "-",
              sttNo: stt.sttNumber,
              totalGrossWeight: stt.grossWeigth,
              destinationCity: stt.destination,
              sttTotalPiece: stt.sttTotalPiece,
              sttGrossWeight: stt.sttGrossWeight,
              sttVolumeWeight: stt.sttVolumeWeight,
              sttProductType: stt.sttProductType,
              sttCommodityName: stt.sttCommodityName,
              sttOriginCityId: stt.sttOriginCityId,
              sttDestinationCityId: stt.sttDestinationCityId
            })
        ),
        totalColumnHeader: 2,
        signature: true
      });
      generateManifestCargoV2(data, barcode, QRCode);
    }
  };

  async printSPTJElektronik(cargoNumber: any) {
    const isPrint = true;
    const cargo: CargoData = await this.getDetailCargo(cargoNumber, isPrint, "");

    if (cargo) {
      const date = new Date();
      const months = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
      const formattedDate = `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;

      const data = {
        name: this.dataProfile.name || "",
        position: "",
        companyName: this.dataProfile.account_type_detail.company_name || "",
        smuNo: cargo.cargoNumber,
        flightNo: cargo.vehiclePoliceNumber,
        route: cargo.destinationCityCode,
        electronicGoods: cargo.nog,
        date: `${startCase(cargo.originCityName.toLowerCase())}, ${formattedDate}`
      };
      await generateSPTJElektronikPDF(data);
    }
  }
  printPtiPdf = async (id: number) => {
    const data: CargoData = await this.getDetailCargo(id, true, "");
    if (data) {
      data.totalActualCargoGrossWeight = this.formatValueNumber(
        data.totalActualCargoGrossWeight
      );

      const companyPlane = await this.companyPlane(data.vehiclePoliceNumber);
      await generatePti({
        companyPlane,
        data,
        accountData: AccountController.accountData
      });
    }
  };

  printSptjNonElectronic = async (id: number) => {
    const data: CargoData = await this.getDetailCargo(id, true, "");
    if (data) {
      data.totalActualCargoGrossWeight = this.formatValueNumber(
        data.totalActualCargoGrossWeight
      );

      const companyPlanes: {
        logo: string;
        label: string;
      }[] = await Promise.all([
        this.companyPlane("JT"),
        this.companyPlane("ID"),
        this.companyPlane("IU", true),
        this.companyPlane("IW"),
        this.companyPlane("LP")
      ]);

      await generateSptjNonElectronic({
        companyPlanes,
        data
      });
    }
  };

  // role account user
  get roleAccount() {
    return (
      (this.dataProfile.account_type === "partner"
        ? this.dataProfile.account_type_detail.type
        : this.dataProfile.account_type) || "internal"
    );
  }
}
